import React, { useContext, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { VERY_SMALL_MOBILE } from "common/constants";
import { formatDateRelative } from "@shared/utils/date";
import Flex from "common/Boxes/Flex";
import { StatusCircle } from "Global/StatusCircle";
import { useHistory } from "react-router-dom";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { typography } from "@shared/common/Themes/typography";
import {
  LanguageNameSpaces,
  Notification,
  NotificationShareTradeData,
} from "@shared/types";
import {
  NotificationPartClicked,
  NotificationPartClickedValueType,
} from "analytics/events";
import AntCaretDownOutlined from "@ant-design/icons/CaretDownOutlined";
import AntCaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import ShareAltOutlined from "@ant-design/icons/ShareAltOutlined";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Timeline from "common/Timeline/Timeline";
import useNotification from "@shared/features/notifications/useNotification";
import CornerStarBanner from "common/misc/CornerStarBanner";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { useShareTradeResults } from "@shared/features/trades/common/hooks";
import { displayResultMessage, openMessage, showIntercom } from "utils/misc";
import Spin from "antd/es/spin";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { useNotificationNavigation } from "features/notifications/hooks.ts";
import {
  useHideBillingButtons,
  useUpgradePlanPath,
} from "features/settings/common/hooks.tsx";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { ChevronRight } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface NotificationItemProps {
  notification: Notification[];
  style?: object;
  showUpgrade?: boolean;
  closeModal?: () => void;
  bannerImageLink?: string;
  setShareTradeData: (shareTradeData: NotificationShareTradeData) => void;
}

const NotificationItem: React.FunctionComponent<NotificationItemProps> = ({
  notification,
  style,
  showUpgrade,
  closeModal,
  bannerImageLink,
  setShareTradeData,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia(
    [`(max-width: ${VERY_SMALL_MOBILE})`],
    [true],
    false
  );
  const history = useHistory();
  const [expandedTimeline, setExpandedTimeline] = useState(false);
  const [expandedSingleNotification, setExpandedSingleNotification] =
    useState(false);
  const { user } = useUser();
  const upgradePath = useUpgradePlanPath();
  const { isNewLook } = useTheme();
  const hideUpgradeButton = useHideBillingButtons();

  const {
    lastNotificationItem,
    isAggregateNotification,
    showBadge,
    clickable,
    showButton,
    buttonText,
    markUnreadNotificationsAsRead,
    delayedExpandedTimeline,
    getTimelineItemColor,
    isLongNotification,
    getSingleNotificationSubMessage,
  } = useNotification(
    notification,
    isMobile ? 38 : 48,
    expandedTimeline,
    expandedSingleNotification,
    70
  );

  const { shareTradeImageLoading, openShareResultsModal } =
    useShareTradeResults(displayResultMessage, openMessage, setShareTradeData);

  const { navigateFromNotification } = useNotificationNavigation();

  const navigateFromLastNotification = () =>
    navigateFromNotification(lastNotificationItem);

  const sendAnalyticsEvent = (
    clickedItem: NotificationPartClickedValueType
  ) => {
    analytics?.singleNotificationClicked({
      notificationType: lastNotificationItem.type,
      notificationPartClicked: clickedItem,
    });
  };

  const onNotificationClick = () => {
    if (clickable) {
      markUnreadNotificationsAsRead();
      if (isAggregateNotification) {
        setExpandedTimeline(!expandedTimeline);
      } else if (isLongNotification) {
        setExpandedSingleNotification(!expandedSingleNotification);
      } else {
        sendAnalyticsEvent(NotificationPartClicked.NOTIFICATION_BOX);
        navigateFromLastNotification();
      }
    }
  };

  const onButtonClick = (e) => {
    e.stopPropagation();
    sendAnalyticsEvent(NotificationPartClicked.NOTIFICATION_C2A_BUTTON);
    if (buttonText?.toLowerCase() === "contact support") {
      showIntercom();
    } else if (lastNotificationItem?.button_link) {
      window.open(lastNotificationItem.button_link, "_blank");
    } else {
      navigateFromLastNotification();
    }
  };

  const icon = (
    <IconWithSource
      mainIconSize={isNewLook ? NEW_LOOK_ICON_SIZE : ICON_SIZE}
      mainImageLink={lastNotificationItem?.main_icon_link}
      sourceImageLink={lastNotificationItem.type_icon_link}
      style={
        isNewLook
          ? {
              flex: "none",
              marginRight: lastNotificationItem.type_icon_link ? 10 : 0,
            }
          : undefined
      }
    />
  );

  const getExpandCollapseIcon = (expanded: boolean) => {
    if (isNewLook) {
      return (
        <NewLookChevron
          size={14}
          strokeWidth={2}
          $expanded={expanded}
          aria-hidden={true}
        />
      );
    }
    return expanded ? <CaretDownOutlined /> : <CaretRightOutlined />;
  };

  const getTimelineItem = (subItem: Notification, isLastSubItem?: boolean) => {
    return {
      color: getTimelineItemColor(subItem),
      content: (
        <Flex $alignItems="flex-start" style={{ width: "100%" }}>
          <div>
            <Message>{subItem.message}</Message>
            <SubMessage>{subItem.sub_message}</SubMessage>
          </div>
          {isLastSubItem ? (
            getExpandCollapseIcon(expandedTimeline)
          ) : (
            <Date>{formatDateRelative(subItem.time, true, true)}</Date>
          )}
        </Flex>
      ),
    };
  };

  const collapsedTimelineItems = notification.slice(1);
  const timelineItems = [getTimelineItem(lastNotificationItem, true)];
  let expandedTimelineItems = collapsedTimelineItems.map((subItem) =>
    getTimelineItem(subItem)
  );

  const timelineNotification = (
    <div style={{ marginTop: 12 }}>
      <MainItemTimeline
        items={timelineItems}
        $expanded={delayedExpandedTimeline}
      />
      <ExpandCollapse expanded={expandedTimeline}>
        <ExpandedTimeline items={expandedTimelineItems} />
      </ExpandCollapse>
    </div>
  );

  const subMessageData = getSingleNotificationSubMessage();

  const singleNotification = isLongNotification ? (
    <>
      <Flex $alignItems="flex-start">
        <Message>{lastNotificationItem.message}</Message>
        {getExpandCollapseIcon(expandedSingleNotification)}
      </Flex>
      <SubMessage>{subMessageData?.subMessage}</SubMessage>
      <ExpandCollapse expanded={expandedSingleNotification}>
        <SubMessage>{subMessageData?.collapsedSubMessage}</SubMessage>
      </ExpandCollapse>
    </>
  ) : (
    <div>
      <Message>{lastNotificationItem.message}</Message>
      <SubMessage>{subMessageData?.subMessage}</SubMessage>
    </div>
  );

  const button = showButton && buttonText && (
    <NotificationButton onClick={onButtonClick}>
      <Flex $justifyContent="flex-start">
        {buttonText}
        {lastNotificationItem?.button_icon_link && (
          <ButtonIcon src={lastNotificationItem?.button_icon_link} />
        )}
      </Flex>
    </NotificationButton>
  );

  const bannerImage = bannerImageLink && (
    <BannerWrapper
      onClick={() => {
        openShareResultsModal(
          lastNotificationItem?.component_id,
          lastNotificationItem?.screen_header || "",
          undefined,
          undefined,
          closeModal
        );
      }}
    >
      <BannerImage src={bannerImageLink} />
      <BannerText>
        <ShareAltOutlined style={{ marginRight: 8 }} />
        {t("labels.share_result")}
        <StyledSpin spinning={shareTradeImageLoading} size="small" />
      </BannerText>
    </BannerWrapper>
  );

  const getUpgradeComponent = () => {
    if (!showUpgrade) return;
    return (
      <UpgradeWrapper>
        <CornerStarBanner />
        Don't miss another trade!{" "}
        {!hideUpgradeButton && (
          <Link
            style={{ marginLeft: 8, fontWeight: isNewLook ? 600 : 700 }}
            onClick={() => {
              history.push(upgradePath);
              closeModal?.();
            }}
          >
            Upgrade Plan
          </Link>
        )}
      </UpgradeWrapper>
    );
  };

  return (
    <Wrapper style={style} $clickable={clickable} $unread={showBadge}>
      <Flex $alignItems="start" style={{ width: "100%" }}>
        <ContentWrapper>
          <IconContentWrapper onClick={onNotificationClick}>
            {icon}
            <Content>
              <TitleDateWrapper $showBadge={showBadge}>
                <Title level={5} $weight={typography.fontWeights.SEMI_BOLD}>
                  {lastNotificationItem.title}
                </Title>
                <Date>
                  {formatDateRelative(lastNotificationItem.time, true, true)}
                </Date>
              </TitleDateWrapper>
              {isAggregateNotification
                ? timelineNotification
                : singleNotification}
              {button}
            </Content>
          </IconContentWrapper>
          {bannerImage}
          {getUpgradeComponent()}
        </ContentWrapper>
        {showBadge && (
          <BadgeWrapper>
            <StatusCircle $animated={!isNewLook} />
          </BadgeWrapper>
        )}
      </Flex>
    </Wrapper>
  );
};

export default NotificationItem;

const ICON_SIZE = 28;
const NEW_LOOK_ICON_SIZE = 32;

const fontStyle = css`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const Wrapper = styled.div<{ $clickable: boolean; $unread?: boolean }>`
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding: 16px 20px;
  display: flex;
  ${(p) => p.$clickable && `cursor: pointer;`}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      position: relative;
      padding: 14px 20px 14px 36px;
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      transition: background-color 120ms;
      background: ${p.$unread
        ? hexToRGB(
            p.theme.colors.primaryBlue,
            p.theme.isDarkMode ? 0.08 : 0.035
          )
        : "transparent"};
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      && h5 {
        ${newLookType.body};
        color: ${getNewLookTokens(p.theme).ink};
        /* Unread rows stand out by weight as well as by their tint. */
        font-weight: ${p.$unread ? 600 : 500};
      }
    `}
`;
const BadgeWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: 0;
  ${(p) =>
    p.theme.isNewLook &&
    `
    top: 27px;
    left: 18px;
    right: auto;
    display: flex;
    > * {
      width: 7px !important;
      height: 7px !important;
      min-width: 0 !important;
    }
  `}
`;
const ContentWrapper = styled.div`
  min-width: 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  flex: auto;
`;
const NotificationButton = styled(Link)`
  margin-top: 8px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  line-height: 28px;
  display: inline-block;
`;
const ButtonIcon = styled.img`
  width: 12px;
  height: auto;
  margin-left: 8px;
`;
const CaretStyle = css`
  color: ${(p) => p.theme.colors.secondaryGrey};
  padding-left: 8px;
`;
const NewLookChevron = styled(ChevronRight)<{ $expanded: boolean }>`
  flex: none;
  margin: 3px 0 0 8px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  transition: transform 150ms;
  ${(p) =>
    p.$expanded &&
    css`
      transform: rotate(90deg);
    `}
`;
const CaretRightOutlined = styled(AntCaretRightOutlined)`
  ${CaretStyle}
`;
const CaretDownOutlined = styled(AntCaretDownOutlined)`
  ${CaretStyle}
`;
const TitleDateWrapper = styled(Flex)<{ $showBadge: boolean }>`
  margin-bottom: 6px;
  padding-right: ${(p) => (p.$showBadge ? "12px" : 0)};
  align-items: flex-start;
  ${(p) =>
    p.theme.isNewLook &&
    `
    column-gap: 8px;
    margin-bottom: 2px;
    padding-right: 0;
    h5 {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  `}
`;
const Date = styled(Text).attrs({ type: "secondary" })`
  ${fontStyle};
  min-width: 26px;
  display: flex;
  justify-content: flex-end;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        ${newLookType.meta};
        color: ${getNewLookTokens(p.theme).muted};
        white-space: nowrap;
      }
    `}
`;
const Content = styled.div`
  width: 100%;
  margin-left: 6px;
  ${(p) => p.theme.isNewLook && `margin-left: 12px; min-width: 0;`}
`;
const IconContentWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
`;
const StyledTimeline = styled(Timeline)`
  width: 100%;

  .ant-steps-item {
    padding-top: 12px;
    min-height: 20px;
  }
  .ant-steps-item-wrapper {
    column-gap: 10px;
  }
  .ant-steps-item-icon {
    margin-block-start: 0;
    background: ${(p) => p.theme.colors.cardBgColor} !important;
    z-index: 2;
  }
  .ant-steps-item-section {
    z-index: 1;
  }
  .ant-timeline-item-content {
    position: relative;
    top: -5px;
  }
  .ant-timeline-item {
    padding-bottom: 0;
  }
`;
const MainItemTimeline = styled(StyledTimeline)<{ $expanded?: boolean }>`
  ${(p) =>
    p.$expanded &&
    `
    .ant-timeline-item-last > .ant-timeline-item-tail {
    display: block;
    height: 130%;
  }
  .ant-steps-item-header::after {
    content: "";
    display: block;
    position: absolute;
    width: 2px;
    height: 100%;
    background: ${p.theme.colors.lightestBorderGrey};
    top: 14px;
    left: 4px;
  }
  `};
`;
const ExpandedTimeline = styled(StyledTimeline)`
  .ant-steps-item-rail {
    height: 100%;
    inset-block-start: unset;
  }
`;
const Message = styled(Text)`
  ${fontStyle};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        ${newLookType.body};
        color: ${getNewLookTokens(p.theme).text};
      }
    `}
`;
const SubMessage = styled(Text)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  line-height: 20px;
  ${fontStyle};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        display: block;
        margin-top: 2px;
        ${newLookType.meta};
        color: ${getNewLookTokens(p.theme).muted};
      }
    `}
`;
const UpgradeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) => (p.theme.isDarkMode ? "#5C6686" : "#EFF4FF")};
  position: relative;
  padding: 16px 20px;
  overflow: hidden;
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    background-color: ${p.theme.colors.lightBlue};
    border-radius: 12px;
    padding: 12px 16px;
  `}
  margin-top: 16px;
  width: 100%;
  font-weight: 500;
`;
const BannerImage = styled.img`
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  z-index: 0;
  width: 100%;
`;
const BannerWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
const BannerText = styled(Text).attrs({ as: "div" })`
  color: ${(p) => p.theme.colors.white};
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  position: absolute;
  padding: 0 20px;
  z-index: 1;
`;
const StyledSpin = styled(Spin)`
  margin-left: 8px;
  color: ${(p) => p.theme.colors.white};
  .ant-spin-dot-item {
    background-color: ${(p) => p.theme.colors.white};
  }
`;
